<template>
  <div>
    <div class="overflow-auto max-h-[40vh]" :class="[ event.status === 'published' ? 'bg-charcoal-100 dark:bg-dark-950' : 'bg-tiger-100 dark:bg-tiger-950 dark:text-tiger-100']">
      <div class="max-w-wrapper-3xs mx-auto flex items-center font-medium text-xs text-left leading-snug p-4">
        <QRBase :content="currentUser?._id" class="h-64 w-full grow shrink-0 mx-auto" v-if="isDisplayQR" />

        <template v-else-if="attendee?.user">
          <UserPicture size="lg" :user="attendee.user" class="shrink-0 self-start" />

          <div class="grow pl-3">
            <p class="text-lg font-bold">
              {{ attendee.user.name }}

              <span class="text-sm" v-if="attendee.guests > 0">{{ $t('components.Event.steps.guests', attendee.guests) }}</span>
            </p>
            <p class="text-sm font-medium">
              {{ $t('components.Event.registeredDate', { date: dayjs(attendee.createdAt).fromNow() }) }}
            </p>

            <TagBase :color="(attendee?.validated ?? 0) < ((attendee?.guests ?? 0) + 1) ? 'pear' : 'tiger'" class="-ml-1 mt-2">
              {{ $t('components.Event.validatedCount', {
                validated: attendee?.validated ?? 0,
                total: (attendee?.guests ?? 0) + 1
              }) }}
            </TagBase>

              <div class="mt-4" v-if="attendeeTransactions.length > 0">
                <TransactionBlock is-simple :transaction="transaction" v-for="transaction in attendeeTransactions"/>
              </div>
          </div>
        </template>
        
        <template v-else>
          <p class="grow">{{ footerText }}</p>

          <ButtonLink size="sm" class="shrink-0" v-if="attendance && attendance.type === 'attending' && attendance?.transactions.length > 0" @click="() => isDisplayTicket = !isDisplayTicket">
            {{ $t('components.Event.steps.tickets') }}

            <IconBase name="cute/angle-right" class="ml-1 transition" :class="[ isDisplayTicket ? 'rotate-90' : '-rotate-90' ]" />
          </ButtonLink>
        </template>
      </div>

      <div class="max-w-wrapper-3xs mx-auto pb-4 px-4 space-y-2" v-if="isDisplayTicket">
        <TransactionBlock v-for="transaction in transactions" :transaction="transaction" />
      </div>
    </div>

    <div>
      <div class="max-w-wrapper-3xs p-2.5 mx-auto flex items-center space-x-2">
        <template v-if="attendee">
          <InputCount v-model="attendeeGuests" :min="1" :max="attendee.guests + 1" class="shrink-0 w-32" />
        
          <ButtonBase color="builders" :is-loading="isLoading" is-full @click="onValidate">
            {{ $t('actions.submit') }}
          </ButtonBase>
        </template>
        <template v-else>
          <ButtonBase
            v-for="button in buttons"
            v-bind="(button as any)"
            @click="() => button.onClickFunction ? button.onClickFunction() : true"
          >
            {{ button.text }}
          </ButtonBase>
        </template>
      </div>
    </div>

    <Teleport to="body" :disabled="!isScanning">
      <div class="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center z-[100]" v-if="isScanning">
        <div class="grow bg-charcoal-950/50 h-full" />
        <div class="flex flex-col shrink-0 h-full">
          <div class="grow bg-charcoal-950/50" />
          <div class="size-[75svw] shrink-0" />
          <div class="grow bg-charcoal-950/50" />
        </div>
        <div class="grow bg-charcoal-950/50 h-full" />

        <div class="absolute bottom-inset-bottom p-6">
          <ButtonBase
            style-type="round"
            color="white"
            @click="() => isScanning = false"
          >
            <IconBase :width="20" name="cute/cross" />
          </ButtonBase>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import EventsStore from '@/store/events'
import SentimentsStore from '@/store/sentiments'
import WindowStore from '@/store/window'
import UsersStore from '@/store/users'
import { ComponentPropsOptions, Event, EventAttendee, User } from '@/classes'
import ButtonBase from '@/components/Button/ButtonBase.vue'
import { Transaction } from '@/classes/Transaction'
import { transactionsEventGetAPI } from '@/api/transactions'
import {
  BarcodeScanner,
  Barcode,
} from '@capacitor-mlkit/barcode-scanning';
import { Capacitor } from '@capacitor/core'
import dayjs from 'dayjs'
import { usersGetAPI } from '~/api/users'
import { Organization } from '~/classes/Organization'

const props = defineProps<{
  event: Event
  organization: Organization | null
  onEnd: () => Promise<boolean>
}>()

const { event, organization } = toRefs(props)

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'previous'): void
}>()

const sentimentsStore = SentimentsStore()
const eventStore = EventsStore()
const userStore = UserStore()
const usersStore = UsersStore()
const windowStore = WindowStore()
const { currentUser } = storeToRefs(userStore)
const { isInvisible } = storeToRefs(windowStore)

const route = useRoute()

const onSave = async (action: 'create' | 'delete') => {
  isLoading.value = true

  await sentimentsStore.create('saved', {
    eventId: event.value._id,
  }, {
    action
  })

  if (action === 'create') {
    useNuxtApp().$amplitude.logEvent(`Event_Save`, {
      source: route.fullPath
    })
  }

  isLoading.value = false
}

const isDisplayTicket = ref(false)
const isDisplayQR = ref(false)
const scanListener = ref()
const isScanning = ref(false)
const attendeeId = ref('')

const attendeeTransactions: Ref<Transaction[]> = ref([])
const attendeeGuests = ref(1)
const attendee: Ref<EventAttendee & { user: User } | null> = ref(null)

watch(attendeeId, async () => {
  attendee.value = null
  attendeeTransactions.value = []

  if (attendeeId.value) {
    const attendance = event.value?.getAttendance(attendeeId.value) ?? null
    attendeeGuests.value = 1 + (attendance?.guests ?? 0)

    if (!attendance) return

    await Promise.all([
      (async () => {
        const response = await transactionsEventGetAPI(event.value._id, attendeeId.value)

        if (response.ok) {
          attendeeTransactions.value = response.data.transactions.map(t => new Transaction(t))
        }
      })(),
      (async () => {
        let user = usersStore.getItem(attendance._id)

        if (!user) {
          const response = await usersGetAPI({
            filters: {
              _id: attendeeId.value
            },
            options: {
              per_page: 1
            }
          })

          if (response.ok && response.data?.users[0]) {
            usersStore.setItem(response.data.users[0])
            user = usersStore.getItem(attendance._id)
          }
        }

        if (!user) return

        attendee.value = {
          ...attendance,
          user
        }
      })()
    ])
  }
}, { immediate: true })

const attendance = computed(() => {
  return event.value?.getAttendance(currentUser.value?._id) ?? null
})

const footerText = computed(() => {
  if (!event.value) return ''

  if (!event.value.canJoin(currentUser.value)) {
    return useNuxtApp().$i18n.t('components.Event.steps.forbidden', {
      genders: (event.value.filters?.genders ?? []).map(g => useNuxtApp().$i18n.t(`components.User.genders.${g}.plural`).toLowerCase()).join(', '),
      min: event.value.filters?.age?.min ?? 18,
      max: event.value.filters?.age?.max ?? 99
    })
  }

  const text = []
  const attendance = event.value.getAttendance(currentUser.value?._id)

  if (event.value.status !== 'published') {
    text.push('⚠️ ' + useNuxtApp().$i18n.t('common.draft'))

    if (event.value.isOrganizing(currentUser.value?._id)) {
      if (event.value.missingFields.length > 0) {
        text.push(useNuxtApp().$i18n.t('components.Event.steps.missingInfo', { content: event.value.missingFields.map(t => ({ title: useNuxtApp().$i18n.t('components.Event.data.title'), description: useNuxtApp().$i18n.t('components.Event.data.description'), image: useNuxtApp().$i18n.t('components.Event.data.image'), place: useNuxtApp().$i18n.t('components.Event.data.place') }[t] )).join(' · ') }))
      } else {
        text.push(useNuxtApp().$i18n.t('components.Event.steps.unavailable'))
      }
    }
  } else if (event.value.isPastAfter) {
    text.push(useNuxtApp().$i18n.t('components.Event.states.past'))

    if (event.value.isAttended(currentUser.value?._id)) text.push(useNuxtApp().$i18n.t('components.Event.states.attended'))

    const attended = event.value.attendingUsers.filter(u => u.type === 'checked-in')
    if (attended.length > 1) text.push(useNuxtApp().$i18n.t('components.Event.attendedCount', attended.length))

  } else if (event.value.isAttending(currentUser.value?._id)) {
    text.push(
      useNuxtApp().$i18n.t('components.Event.states.attending') +
      (attendance && attendance.guests > 0 ? ` (${useNuxtApp().$i18n.t('components.Event.steps.guests', attendance.guests)})` : '')
    )
  } else {
    if (!event.value.isPast && event.value.remainingSpots <= 0 && !event.value.isAttending(currentUser.value?._id)) {
      if (event.value.waitingCount > 0 ) {
        text.push(useNuxtApp().$i18n.t('components.Event.waitingCount', event.value.waitingCount))
      } else {
        text.push(useNuxtApp().$i18n.t('components.Event.states.full'))
      }
    } else if (!event.value.isPast && event.value.remainingSpots <= 20 && event.value.remainingSpots > 0 && !event.value.isAttending(currentUser.value?._id)) {
      text.push(useNuxtApp().$i18n.t('components.Event.remainingCount', event.value.remainingSpots))
    }

    if ((event.value.products ?? []).length <= 0) {
      text.push(useNuxtApp().$i18n.t('components.Event.steps.free', event.value.remainingSpots))
    } else {
      const minPrice = event.value.products.reduce((acc, price) => {
        return price.amount < acc ? price.amount : acc
      }, Infinity)

      text.push(useNuxtApp().$i18n.t('price.from', { price: minPrice + '€' }))
    }
  }

  return text.join(' · ')
})

type ButtonProps = ComponentPropsOptions<typeof ButtonBase> & {
  text?: string
  onClickFunction?: () => void
}

const isLoading = ref(false)

const buttons: ComputedRef<ButtonProps[]> = computed(() => {
  if (!event.value) return []

  if (!event.value.canJoin(currentUser.value)) return []

  const buttons = []

  if (event.value.status !== 'published') {
    if (event.value.isOrganizing(currentUser.value?._id)) {

      if (event.value.missingFields.length <= 0) {
        buttons.push({
          color: 'white',
          icon: 'cute/pen',
          class: 'shrink-0',
          styleType: 'round',
          to: { name: 'app-events-slug-edit', params: { slug: event.value.slug } }
        })

        if (!event.value.organization || organization.value?.isPro) {
          buttons.push({
            text: useNuxtApp().$i18n.t('components.Event.actions.publish'),
            color: 'base',
            icon: 'cute/check',
            isLoading: isLoading.value,
            isFull: true,
            onClickFunction: async () => {
              isLoading.value = true

              await eventStore.create({ status: 'published' }, event.value?._id)

              isLoading.value = false
            }
          })
        }
      } else {
        buttons.push({
          color: 'white',
          text: useNuxtApp().$i18n.t('components.Event.actions.edit'),
          isFull: true,
          icon: 'cute/pen',
          to: { name: 'app-events-slug-edit', params: { slug: event.value.slug } }
        })
      }
    }
  } else if (event.value.isValidationNeeded(currentUser.value?._id)) {
    if (Capacitor.isNativePlatform()) {
      buttons.push({
        text: useNuxtApp().$i18n.t('actions.scan'),
        icon: 'cute/qr',
        class: 'shrink-0',
        onClickFunction: async () => {
          try {
            isScanning.value = true

            const result: Barcode | null = await new Promise(async resolve => {
              scanListener.value = await BarcodeScanner.addListener('barcodeScanned', async (result: any) => {
                  isScanning.value = false
                  resolve(result.barcode as Barcode)
                }
              )

              let permissions = await BarcodeScanner.checkPermissions()
              if (permissions.camera !== 'granted') {
                permissions = await BarcodeScanner.requestPermissions()
              }

              if (permissions.camera === 'granted') {
                document.querySelector('body')?.classList.add('barcode-scanner-active')
                isInvisible.value = true
                return await BarcodeScanner.startScan()
              } else {
                isScanning.value = false
                return ''
              }
            })

            if (result) attendeeId.value = result.displayValue
          } catch (e) {
            console.error(e)
            isScanning.value = false
          }
        },
        color: 'builders'
      })
    }
    
    buttons.push({
      text: useNuxtApp().$i18n.t('components.Event.actions.validate'),
      color: 'white',
      onClickFunction: () => emit('next'),
      isFull: true
    })
  } else if (event.value.isReviewNeeded(currentUser.value?._id)) {
    buttons.push({
      text: useNuxtApp().$i18n.t('components.Event.actions.review'),
      color: 'builders',
      onClickFunction: () => emit('next'),
      isFull: true
    })
  } else if (event.value.isPastAfter || event.value.isAttended(currentUser.value?._id)) {
    if (event.value.isOrganizing(currentUser.value?._id) && event.value.reviews.length > 0) {
      buttons.push({
        text: useNuxtApp().$i18n.t('components.Event.actions.reviewShow'),
        color: 'base',
        onClickFunction: () => emit('next'),
        isFull: true
      })
    } else {
      buttons.push({
        text: event.value.isOrganizing(currentUser.value?._id) ? useNuxtApp().$i18n.t('components.Event.actions.thankYouOrganizer') : event.value.isReviewed(currentUser.value?._id) ? useNuxtApp().$i18n.t('components.Event.actions.thankYouReview') : useNuxtApp().$i18n.t('components.Event.actions.past'),
        color: 'weak',
        isDisabled: true,
        isFull: true
      })
    }
  } else if (event.value.isAttending(currentUser.value?._id)) {
    if (event.value.isOrganizing(currentUser.value?._id)) {
      buttons.push({
        color: 'white',
        text: useNuxtApp().$i18n.t('components.Event.actions.edit'),
        icon: 'cute/pen',
        isFull: true,
        to: { name: 'app-events-slug-edit', params: { slug: event.value.slug } }
      })
    } else {
      if (!isDisplayQR.value) {
        buttons.push({
          text: useNuxtApp().$i18n.t('components.Event.actions.ticket'),
          icon: 'cute/qr',
          class: 'shrink-0',
          onClickFunction: () => isDisplayQR.value = true,
          color: 'builders'
        })
        
        buttons.push({
          text: useNuxtApp().$i18n.t('components.Event.actions.updateAttendance'),
          icon: 'cute/pen',
          onClickFunction: () => emit('next'),
          color: 'white',
          isFull: true
        })
      } else {
        buttons.push({
          text: useNuxtApp().$i18n.t('actions.close'),
          isFull: true,
          onClickFunction: () => isDisplayQR.value = false,
          color: 'white'
        })
      }
    }
  } else if (event.value.isWaiting(currentUser.value?._id)) {
    buttons.push({
      text: useNuxtApp().$i18n.t('components.Event.actions.waiting'),
      icon: 'cute/pen',
      onClickFunction: () => emit('next'),
      color: 'royal',
      isFull: true
    })
  } else {
    buttons.push({
      color: 'white',
      class: 'shrink-0',
      styleType: 'round',
      isLoading: isLoading.value,
      onClickFunction: () => event.value.hasSaved ? onSave('delete') : onSave('create'),
      icon: event.value.hasSaved ? 'cute/bookmark-fill' : 'cute/bookmark',
      iconClass: event.value.hasSaved ? 'text-royal-600' : '',
    })

    if (event.value.remainingSpots <= 0) {
      if (event.value.products.length <= 0) {
        buttons.push({
          text: useNuxtApp().$i18n.t('components.Event.actions.waitingList'),
          onClickFunction: async () => {
            const result = await props.onEnd()
            if (result) emit('next')
          },
          color: 'royal',
          isFull: true
        })
      } else {
        buttons.push({
          text: useNuxtApp().$i18n.t('components.Event.actions.full'),
          color: 'weak',
          isDisabled: true,
          isFull: true
        })
      }
    } else {
      buttons.push({
        text: event.value.products.length > 0 ? useNuxtApp().$i18n.t('components.Event.actions.prices') : useNuxtApp().$i18n.t('components.Event.actions.attend'),
        color: 'base',
        onClickFunction: async () => {
          const result = await props.onEnd()
          if (result) emit('next')
        },
        isFull: true
      })
    }
  }

  return (buttons as ButtonProps[]).slice(0, 2) 
})

const transactions: Ref<Transaction[]> = ref([])

watch(isDisplayTicket, async (v) => {
  if (v) {
    const response = await transactionsEventGetAPI(event.value._id)

    if (response.ok) {
      transactions.value = response.data.transactions.map(t => new Transaction(t))
    }
  }
})

watch(isScanning, async () => {
  if (!isScanning.value) {
    if (scanListener.value) scanListener.value.remove()
    
    document.querySelector('body')?.classList.remove('barcode-scanner-active')
    
    await BarcodeScanner.stopScan()

    isInvisible.value = false
  }
})

const onValidate = async () => {
  isLoading.value = true 

  const response = await eventStore.attendSet(event.value._id, {
    [attendeeId.value]: 'checked-in'
  }, {
    validationCount: attendeeGuests.value
  })

  if (response.ok) {
    attendeeId.value = ''
  }

  isLoading.value = false 

}

</script>